import React, { useEffect, useRef } from 'react';
import { prefersReducedMotion } from './useReveal';

const DURATION_MS = 1500;
const DAMPING = 4.5;
const FREQUENCY = 13;

// A needle finding its mark: the value starts off the target, overshoots it
// slightly and settles. Frames write straight to the DOM node, so React
// never re-renders while it moves. Under reduced motion, or without
// IntersectionObserver, the final text is simply what renders.
//
// value:  numeric target. from: numeric start value.
// format: (number) => string used on frames. text: exact final string.
export default function BallisticNumber({ value, from, format, text, className = '' }) {
    const ref = useRef(null);

    useEffect(() => {
        const node = ref.current;
        if (!node || prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }
        let frame = 0;
        let observer;

        const run = () => {
            const start = performance.now();
            const gap = from - value;
            const tick = now => {
                const t = Math.min((now - start) / 1000, DURATION_MS / 1000);
                if (now - start >= DURATION_MS) {
                    node.textContent = text;
                    return;
                }
                const decay = Math.exp(-DAMPING * t) * Math.cos(FREQUENCY * t);
                node.textContent = format(value + gap * decay);
                frame = requestAnimationFrame(tick);
            };
            node.textContent = format(from);
            frame = requestAnimationFrame(tick);
        };

        observer = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    observer.disconnect();
                    run();
                }
            },
            { threshold: 0.4 }
        );
        observer.observe(node);

        return () => {
            observer.disconnect();
            cancelAnimationFrame(frame);
            node.textContent = text;
        };
    }, [value, from, format, text]);

    return (
        <span ref={ref} className={`lp-num ${className}`}>
            {text}
        </span>
    );
}
